import React, { useEffect, useState, useCallback } from 'react';
import { SettingCard } from '../../components/SettingCards/SettingCard/SettingCard';
import { SettingCardContent } from '../../components/SettingCards/SettingCard/SettingCardContent/SettingCardContent';
import { Dropdown, DropdownOption } from '../../components/Dropdown/Dropdown';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { SettingsLogger, logConstants, AotCategory } from '../../utils/SettingsLogger';
import {
	getSettingConfig,
	DropdownSettingConfig,
} from '../../config/SettingsDefinitions';
import { CAMERA_SETTING_IDS } from '../../config/SettingIds';
import { ToggleSettingGroup } from '../../components/SettingCards/ToggleSetting/ToggleSettingGroup';
import './CameraSetting.css';
import { DropdownSettingValue } from '../../components/SettingCards/DropdownSetting/DropdownSetting';

export const CameraSetting: React.FC = () => {
	const [cameraOptions, setCameraOptions] = useState<DropdownOption[]>([]);
	const [selectedCamera, setSelectedCamera] = useState<string>('');
	const [disabled, setDisabled] = useState<boolean>(false);
	const [backgroundBlurEnabled, setBackgroundBlurEnabled] = useState<boolean>(false);

	const config = getSettingConfig(
		CAMERA_SETTING_IDS.AVAILABLE_CAMERA
	) as DropdownSettingConfig;

	const loadCameraSetting = useCallback(async () => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			const response = await bridge.getSettings({
				settings: [
					{ settingKey: CAMERA_SETTING_IDS.AVAILABLE_CAMERA },
					{ settingKey: CAMERA_SETTING_IDS.BACKGROUND_BLUR },
				],
			});

			if (response.status === 0 && response.data?.settings) {
				const cameraItem = response.data.settings[0];
				const blurItem = response.data.settings[1];

				if (cameraItem?.settingValue) {
					const parsedValue: DropdownSettingValue =
						typeof cameraItem.settingValue === 'string'
							? JSON.parse(cameraItem.settingValue)
							: cameraItem.settingValue;

					setCameraOptions(parsedValue.availableOptions || []);
					setSelectedCamera(parsedValue.selectedOption || '');
					setDisabled(!cameraItem.enabled);
				}

				if (blurItem?.settingValue) {
					setBackgroundBlurEnabled(blurItem.settingValue === 'true');
				}
			}
		} catch (err) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to load camera setting: ${err}`,
				AotCategory.Error,
				`Failed to load camera setting: ${err}`
			);
		}
	}, []);

	useEffect(() => {
		loadCameraSetting();
	}, [loadCameraSetting]);

	const handleCameraChange = async (value: string) => {
		setSelectedCamera(value);

		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();

			// Send only the camera selection
			const updatedValue: DropdownSettingValue = {
				selectedOption: value,
				availableOptions: cameraOptions,
			};

			const response = await bridge.setSetting({
				settingKey: CAMERA_SETTING_IDS.AVAILABLE_CAMERA,
				settingValue: JSON.stringify(updatedValue),
			});

			if (response.status !== 0) {
				SettingsLogger.getInstance().write(
					logConstants.ERROR,
					'Failed to save camera setting'
				);
				// Revert on error
				await loadCameraSetting();
			}
		} catch (err) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to save camera setting: ${err}`,
				AotCategory.Error,
				`Failed to save camera setting: ${err}`
			);
			// Revert on error
			await loadCameraSetting();
		}
	};

	const handleOnCameraBlurChange = async (value: boolean) => {
		setBackgroundBlurEnabled(value);
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			bridge.setSetting({
				settingKey: CAMERA_SETTING_IDS.BACKGROUND_BLUR,
				settingValue: value,
			});
		} catch (err) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to change camera blur setting: ${err}`,
				AotCategory.Error,
				`Failed to change camera blur setting: ${err}`
			);
		}
	};

	return (
		<SettingCard
			title={config.title}
			description={config.description}
			settingId={CAMERA_SETTING_IDS.AVAILABLE_CAMERA}
			disabled={disabled}
			content={
				<SettingCardContent>
					<>
						<Dropdown
							className="camera-setting-dropdown"
							options={cameraOptions}
							value={selectedCamera}
							onChange={handleCameraChange}
							disabled={disabled}
						/>

						<ToggleSettingGroup
							className="camera-setting-toggle-group"
							settings={[
								{
									settingId: CAMERA_SETTING_IDS.BACKGROUND_BLUR,
									title: 'Background Blur',
									description: '',
									checked: backgroundBlurEnabled,
									onChange: handleOnCameraBlurChange,
									disabled: disabled,
								},
							]}
						/>
					</>
				</SettingCardContent>
			}
		/>
	);
};
